import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useUpdateConcept } from "@/hooks/useConcepts";
import type { Concept } from "@/types/concept";

interface Props {
  concept: Concept;
  hideTitle?: boolean;
  paperCount?: number;
  onRefineClick?: () => void;
}

export function ConceptInfo({ concept, hideTitle, paperCount, onRefineClick }: Props) {
  const updateConcept = useUpdateConcept(concept.id);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const [keywords, setKeywords] = useState(concept.keywords);

  const debouncedUpdate = useCallback(
    (data: { abstract?: string; keywords?: string }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateConcept.mutate(data);
      }, 1500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable
    [updateConcept.mutate],
  );

  const editor = useEditor({
    extensions: [StarterKit],
    content: concept.abstract || "<p></p>",
    onUpdate: ({ editor }) => {
      debouncedUpdate({ abstract: editor.getHTML() });
    },
    editorProps: {
      attributes: {
        class:
          "min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring prose prose-sm max-w-none",
      },
    },
  });

  // Sync editor and keywords when concept data changes (e.g. after restore)
  useEffect(() => {
    if (editor && concept.abstract !== editor.getHTML()) {
      editor.commands.setContent(concept.abstract || "<p></p>");
    }
  }, [concept.id, concept.abstract, editor]);

  useEffect(() => {
    setKeywords(concept.keywords);
  }, [concept.id, concept.keywords]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleKeywordsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setKeywords(e.target.value);
      debouncedUpdate({ keywords: e.target.value });
    },
    [debouncedUpdate],
  );

  return (
    <div className="space-y-3">
      {!hideTitle && <h2 className="text-xl font-semibold">{concept.name}</h2>}

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Research question</Label>
        <EditorContent editor={editor} />
      </div>

      <div className="space-y-1">
        <Label htmlFor="keywords" className="text-xs text-muted-foreground">
          Search terms
        </Label>
        <Input
          id="keywords"
          value={keywords}
          onChange={handleKeywordsChange}
          placeholder="e.g. deep learning, clinical trials"
          className="text-sm"
        />
      </div>

      {onRefineClick && paperCount != null && paperCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onRefineClick}
          data-guide-target="refineConceptButton"
        >
          <Sparkles className="mr-1.5 size-3.5" />
          Refine with AI
        </Button>
      )}
    </div>
  );
}
